import React, { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, Snackbar, TextField, Typography, Alert } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { ReportBlockFieldDesc } from '../../../types/reporttype';
import Toolbar from '../toolbar/Toolbar';
import { PopulateFieldMethod } from '../blocks/reportsblock';
import VerticalSplittedComparison from './VerticalSplittedComparison';
import DiffView from './diffview';

const useStyles = makeStyles(() => ({
  editor: {
    margin: 'auto',
    width: 'calc(100% - 16px)',
    backgroundColor: 'transparent !important'
  },
  fieldContent: {
    paddingRight: '16px',
    maxWidth: 'calc(100% - 54px)',
    flexBasis: 'calc(100% - 54px)',
    '& p': {
      margin: '0',
      padding: '0',
      fontSize: '14px'
    }
  }
}));

interface ReportBlockStringFieldProps {
	desc: ReportBlockFieldDesc,
	previousValue: string,
	currentValue: string,
	isEditable?: boolean,
	onSave: (value : string | undefined) => void,
	populateField?: PopulateFieldMethod,
	isEachFieldsSplitted?: boolean,
  isDiffView?: boolean
}

function ReportBlockStringField(props: ReportBlockStringFieldProps):ReactElement {
  const { t } = useTranslation();
  const isPopulate = props.desc.populate;
  const classes = useStyles();
  const { isEditable } = props;
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const { previousValue, currentValue } = props;
  const initialValue = currentValue || previousValue;
  const [text, setText] = useState<string | undefined>(initialValue);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [isVerticalSplitOpened, setIsVerticalSplitOpened] = useState<boolean>(!!props.isEachFieldsSplitted);
  const [isDiffView, setIsDiffView] = useState<boolean>(!!props.isDiffView);
  const [dispOnlyChanges, setDispOnlyChanges] = useState(false);

  const editAction = () => {
    setIsEditing(true);
    setIsVerticalSplitOpened(false);
    setIsDiffView(false);
  };

  const cancelAction = () => {
    setText(initialValue);
    setIsEditing(false);
  };

  const populateAction = () => {
    if (props.populateField){
      props.populateField(props.desc.id)
        .then((apiResp) => {
          const populateText = apiResp.data? apiResp.data: undefined;
          setText(populateText);
          setIsEditing(true);
        })
        .catch((err) => {
          console.log(err);
          setSnackbarOpen(true);
        });
    }
  };

  const saveAction = () => {
    if (text) {
      props.onSave( text );
    } else {
      props.onSave( undefined );
    }
    setIsEditing(false);
  };

  useEffect(() => {
    setIsVerticalSplitOpened(!!props.isEachFieldsSplitted);
    setIsDiffView(!!props.isDiffView);
  }, [props.isEachFieldsSplitted, props.isDiffView]);

  return (
    <Grid container>
      <Grid
        item sm={11}
        style={{ maxWidth: 'calc(100% - 54px)', flexBasis: 'calc(100% - 54px)' /* borderRight: '1px solid grey' */ }}>
        <Snackbar onClose={() => setSnackbarOpen(false)} autoHideDuration={6000} open={snackbarOpen} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert onClose={() => setSnackbarOpen(false)} style={{ marginTop: '60px' }} severity='error'>
            {t('dds.tabs.reports.text.nothingReceived')}
          </Alert>
        </Snackbar>
        {	isEditing && !isVerticalSplitOpened &&
					<TextField
					  className={classes.editor}
					  value={text}
					  disabled={!isEditing}
					  onChange={e => setText(e.target.value)}
					  fullWidth
					  variant='outlined'
					/>}
        {!isEditing && !isVerticalSplitOpened &&
					<Typography>
					  {initialValue}
					</Typography>
        }
        {currentValue && (isVerticalSplitOpened || isDiffView) &&
          <>
            {isVerticalSplitOpened &&
              <VerticalSplittedComparison previousValue={previousValue} currentValue={currentValue} />
            }
            {isDiffView &&
              <DiffView previousValue={previousValue} currentValue={currentValue} dispOnlyChanges={dispOnlyChanges} />
            }
          </>
        }
      </Grid>
      <Grid
        item sm={1}
        style={{ maxWidth: '54px', flexBasis: '54px' }}>
        <Toolbar
          isEditable={ isEditable }
          isEditing={ isEditing }
          editAction={ editAction }
          cancelAction={ cancelAction }
          saveAction={ saveAction }
          isPopulate={ isPopulate }
          populateAction={populateAction}
          setSplitAction={setIsVerticalSplitOpened}
          setDiffView={setIsDiffView}
          isSplitOptionAvailable={!!currentValue}
          isVerticalSplitOpened={isVerticalSplitOpened}
          isDiffView={isDiffView}
          dispOnlyChanges={dispOnlyChanges}
          setDispOnlyChanges={setDispOnlyChanges}
          isHorizontal
          isSaveNeeded
        />
      </Grid>
    </Grid>
  );
}

export default ReportBlockStringField;
